import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Grid,
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
    useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PaymentIcon from '@mui/icons-material/Payment';
import EventIcon from '@mui/icons-material/Event';
import FieldView2 from 'core/components/layout/FieldView2';
import { getTransactionListAPI } from 'api/CoreMlmmgmtWebServices';
import { convertToRS } from 'core/js/FormUtils';

export default function SuccessPayment() {
    const { mlmuserObj } = useSelector((state) => state.dmpslice.mlmuserDashboard);
    const [paymentObj, setPaymentObj] = React.useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        getTransactionListAPI({
            "paymentOrderId": mlmuserObj.paymentorderid
        },
        (response) => {
            console.log("Transaction List API Response:", response);
            setPaymentObj(response.paymentObj);
        },
        (error) => {
            console.error("Transaction List API Error:", error);
        }); 
    },[]);
    
    const formatValue = (value) => {
        return value !== null && value !== undefined && value !== '' && value !== 'null' ? value : '-';
    };

    // Add date formatting function
    const formatDate = (dateString) => {
        if (!dateString || dateString === 'null') return '-';
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const getPaymentStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'captured':
            case 'success':
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'failed':
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'captured':
            case 'success':
            case 'completed':
                return <CheckCircleIcon sx={{ color: 'success.main', fontSize: '20px' }} />;
            case 'failed':
            case 'error':
                return <ErrorIcon sx={{ color: 'error.main', fontSize: '20px' }} />;
            default:
                return <PaymentIcon sx={{ color: 'warning.main', fontSize: '20px' }} />;
        }
    };

    const renderMobileTransactionCards = () => {
        if (!paymentObj?.txnList || paymentObj.txnList.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No transaction history available
                </Typography>
            );
        }

        // Reverse the array to show latest transactions first
        const sortedTransactions = [...paymentObj.txnList].reverse();

        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'grey.100',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'grey.400',
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: 'grey.600',
                        },
                    },
                }}
            >
                {sortedTransactions.map((txn, index) => (
                    <Card key={index} sx={{ boxShadow: 2, flexShrink: 0 }}>
                        <CardContent sx={{ p: 2 }}>
                            {/* Status and Amount Row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getStatusIcon(txn.paymentStatus)}
                                    <Chip
                                        label={txn.paymentStatus}
                                        color={getPaymentStatusColor(txn.paymentStatus)}
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {txn.amount && txn.amount !== 'null' ? convertToRS(txn.amount / 100) : '-'}
                                </Typography>
                            </Box>

                            {/* Payment Date */}
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Payment Date
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EventIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                        {formatDate(txn.date)}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Payment ID */}
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Payment ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                    {formatValue(txn.paymentId)}
                                </Typography>
                            </Box>

                            {/* Description */}
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Description
                                </Typography>
                                <Typography variant="body2">
                                    {formatValue(txn.description)}
                                </Typography>
                            </Box>

                            {/* Error Details */}
                            {txn.errorCode && txn.errorCode !== 'null' ? (
                                <Box sx={{ 
                                    backgroundColor: 'error.lighter', 
                                    borderRadius: 1, 
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'error.light'
                                }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                        Error Details
                                    </Typography>
                                    <Chip
                                        label={txn.errorCode}
                                        color="error"
                                        size="small"
                                        sx={{ mb: 1, fontWeight: 600 }}
                                    />
                                    {txn.errorReason && txn.errorReason !== 'null' && (
                                        <Typography variant="caption" display="block" color="error.main" sx={{ mb: 0.5 }}>
                                            <strong>Reason:</strong> {txn.errorReason}
                                        </Typography>
                                    )}
                                    {txn.errorDescription && txn.errorDescription !== 'null' && (
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {txn.errorDescription}
                                        </Typography>
                                    )}
                                    {txn.errorStep && txn.errorStep !== 'null' && (
                                        <Typography variant="caption" display="block" color="warning.main">
                                            <strong>Step:</strong> {txn.errorStep}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    backgroundColor: 'success.lighter', 
                                    borderRadius: 1, 
                                    p: 1,
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                        ✓ No errors
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    };

    const renderDesktopTransactionTable = () => {
        if (!paymentObj?.txnList || paymentObj.txnList.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No transaction history available
                </Typography>
            );
        }

        // Reverse the array to show latest transactions first
        const sortedTransactions = [...paymentObj.txnList].reverse();

        return (
            <TableContainer 
                component={Paper} 
                sx={{ 
                    boxShadow: 2,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'grey.100',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'grey.400',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: 'grey.600',
                        },
                    },
                }}
            >
                <Table sx={{ minWidth: 750 }} aria-label="transaction history table" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Payment Date</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Payment ID</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Amount</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Description</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', backgroundColor: 'primary.main' }}>Error Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedTransactions.map((txn, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: 'grey.50',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'primary.50',
                                    },
                                }}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusIcon(txn.paymentStatus)}
                                        <Chip
                                            label={txn.paymentStatus}
                                            color={getPaymentStatusColor(txn.paymentStatus)}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EventIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                            {formatDate(txn.date)}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                        {formatValue(txn.paymentId)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        {txn.amount && txn.amount !== 'null' ? convertToRS(txn.amount / 100) : '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatValue(txn.description)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {txn.errorCode && txn.errorCode !== 'null' ? (
                                        <Box>
                                            <Chip
                                                label={txn.errorCode}
                                                color="error"
                                                size="small"
                                                sx={{ mb: 0.5, fontWeight: 600 }}
                                            />
                                            {txn.errorReason && txn.errorReason !== 'null' && (
                                                <Typography variant="caption" display="block" color="error.main">
                                                    <strong>Reason:</strong> {txn.errorReason}
                                                </Typography>
                                            )}
                                            {txn.errorDescription && txn.errorDescription !== 'null' && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {txn.errorDescription}
                                                </Typography>
                                            )}
                                            {txn.errorStep && txn.errorStep !== 'null' && (
                                                <Typography variant="caption" display="block" color="warning.main">
                                                    <strong>Step:</strong> {txn.errorStep}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="success.main">
                                            No errors
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Grid container sx={{width:'100%'}}>
            <Grid item xs={12} sx={{  }}>
                <Typography variant="h5" gutterBottom sx={{fontWeight:600}}>
                    Payment Information (Razorpay)
                </Typography>
                <Divider />
            </Grid>
            
            {/* Transaction History Section */}
            <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <PaymentIcon sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Transaction History
                    </Typography>
                    {paymentObj?.txnList && (
                        <Chip 
                            label={`${paymentObj.txnList.length} transaction${paymentObj.txnList.length !== 1 ? 's' : ''}`}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>
                <Divider sx={{ mt: 1 }} />
            </Grid>
            <Grid item xs={12}>
                {isMobile ? renderMobileTransactionCards() : renderDesktopTransactionTable()}
            </Grid>
        </Grid>
    );
}